import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, IndianRupee, Save, MessageCircle, ExternalLink, Trophy } from "lucide-react";
import { toast } from "sonner";

const URL_PREFIX_RE = /^https?:\/\/.+/i;

// Fields grouped so the page tells a clean story: campaign billing on
// top (the money-earning model), then per-message rates + caps, then the
// WhatsApp lock, then the legacy fields at the bottom.
const CAMPAIGN_FIELDS = [
  { key: "campaign_fee", label: "Campaign fee", suffix: "/ campaign (after trial)" },
  { key: "trial_days", label: "Trial length", suffix: "days", integer: true },
  { key: "trial_lead_cap", label: "Trial lead cap", suffix: "leads per trial campaign", integer: true },
  { key: "paid_campaign_lead_cap", label: "Paid campaign lead cap", suffix: "leads included with a paid campaign", integer: true },
  { key: "lead_overage_rate", label: "Lead overage rate", suffix: "₹ per lead beyond the cap (from wallet)" },
  { key: "gst_percentage", label: "GST rate", suffix: "% added at checkout", integer: true },
];

const MESSAGE_FIELDS = [
  { key: "email_rate", label: "Email retargeting", suffix: "/ email" },
  { key: "whatsapp_rate", label: "WhatsApp retargeting", suffix: "/ message" },
  { key: "sms_rate", label: "SMS retargeting", suffix: "/ message" },
  { key: "rcs_rate", label: "RCS retargeting", suffix: "/ message" },
  { key: "email_cap_per_campaign", label: "Email cap per campaign", suffix: "emails max per campaign", integer: true },
];

const LEGACY_FIELDS = [
  { key: "setup_fee", label: "Setup fee (legacy)", suffix: "/ vendor" },
  { key: "per_lead_rate", label: "Per lead (legacy)", suffix: "/ spin captured" },
];

export default function SuperAdminPricingPage() {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  // A4 — featured-slot rank pricing lives in a separate mongo doc
  // (`global_pricing.featured_slot_*`) so it stays independent from
  // the campaign-fee / GST fields. Fetched + saved via its own API.
  const [featured, setFeatured] = useState(null);
  const [featuredBusy, setFeaturedBusy] = useState(false);

  useEffect(() => {
    api.superadminGetPricing().then((r) => setPricing(r.data));
    api.featuredPricing().then((r) => {
      const rp = r.data?.rank_prices_paise || {};
      setFeatured({
        rank_1_rupees: (rp["1"] || 0) / 100,
        rank_2_rupees: (rp["2"] || 0) / 100,
        rank_3_rupees: (rp["3"] || 0) / 100,
        duration_days: r.data?.duration_days || 30,
      });
    }).catch(() => {
      setFeatured({ rank_1_rupees: 5000, rank_2_rupees: 3000, rank_3_rupees: 1500, duration_days: 30 });
    });
  }, []);

  const saveFeatured = async () => {
    if (!featured) return;
    setFeaturedBusy(true);
    try {
      const payload = {
        rank_prices_paise: {
          "1": Math.max(0, Math.round(Number(featured.rank_1_rupees) * 100)),
          "2": Math.max(0, Math.round(Number(featured.rank_2_rupees) * 100)),
          "3": Math.max(0, Math.round(Number(featured.rank_3_rupees) * 100)),
        },
        duration_days: Math.max(1, Math.round(Number(featured.duration_days) || 30)),
      };
      await api.superadminSetFeaturedPricing(payload);
      toast.success("Featured-slot pricing updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save featured pricing");
    } finally {
      setFeaturedBusy(false);
    }
  };

  const save = async () => {
    // Validate the redirect URL BEFORE hitting the network — surfaces
    // the error without triggering a needless round-trip.
    const redirectUrl = (pricing.post_spin_redirect_url || "").trim();
    if (!URL_PREFIX_RE.test(redirectUrl)) {
      toast.error("Post-spin redirect URL must start with http:// or https://");
      return;
    }
    setBusy(true);
    try {
      await api.superadminSetPricing({
        setup_fee: Number(pricing.setup_fee) || 0,
        per_lead_rate: Number(pricing.per_lead_rate) || 0,
        sms_rate: Number(pricing.sms_rate) || 0,
        rcs_rate: Number(pricing.rcs_rate) || 0,
        campaign_fee: Number(pricing.campaign_fee) || 499,
        trial_days: Number(pricing.trial_days) || 15,
        trial_lead_cap: Number(pricing.trial_lead_cap) || 300,
        email_cap_per_campaign: Number(pricing.email_cap_per_campaign) || 500,
        email_rate: Number(pricing.email_rate) || 0.15,
        whatsapp_rate: Number(pricing.whatsapp_rate) || 1.75,
        whatsapp_pricing_confirmed: !!pricing.whatsapp_pricing_confirmed,
        paid_campaign_lead_cap: Number(pricing.paid_campaign_lead_cap) || 1000,
        lead_overage_rate: Number(pricing.lead_overage_rate) || 1.0,
        sms_pricing_confirmed: !!pricing.sms_pricing_confirmed,
        rcs_pricing_confirmed: !!pricing.rcs_pricing_confirmed,
        gst_percentage: Number(pricing.gst_percentage) || 18,
        post_spin_redirect_url: redirectUrl,
      });
      toast.success("Pricing updated");
      // Re-read the doc so `updated_at` reflects the write we just made.
      const fresh = await api.superadminGetPricing();
      setPricing(fresh.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!pricing) {
    return (
      <div className="min-h-[400px] grid place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  const renderField = (f) => (
    <div key={f.key} className="flex items-center gap-3">
      <div className="flex-1 space-y-1.5">
        <Label>{f.label}</Label>
        <div className="relative">
          <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="number"
            step={f.integer ? "1" : "0.01"}
            min={0}
            value={pricing[f.key] ?? ""}
            onChange={(e) => setPricing({ ...pricing, [f.key]: e.target.value })}
            className="pl-9"
            data-testid={`sa-pricing-${f.key}`}
          />
        </div>
        <p className="text-xs text-muted-foreground">{f.suffix}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Global pricing</h1>
        <p className="text-muted-foreground text-sm">
          Applies to all new billing cycles. Existing bills / paid campaigns are not changed.
        </p>
      </div>

      <Card className="p-5 max-w-2xl space-y-4" data-testid="sa-pricing-campaign-card">
        <h2 className="font-heading text-base font-semibold">Campaign billing</h2>
        {CAMPAIGN_FIELDS.map(renderField)}
      </Card>

      <Card className="p-5 max-w-2xl space-y-4" data-testid="sa-pricing-messages-card">
        <h2 className="font-heading text-base font-semibold">Retargeting rates &amp; caps</h2>
        {MESSAGE_FIELDS.map(renderField)}
        <div className="flex items-start gap-3 rounded-lg border border-border p-3 bg-secondary/40">
          <MessageCircle className="h-5 w-5 text-primary mt-0.5" />
          <div className="flex-1">
            <Label className="text-sm font-semibold">WhatsApp pricing confirmed</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Hard-lock: WhatsApp retargeting is disabled for all vendors until you flip this on.
              Vendors see a &quot;Coming soon&quot; label on the channel until then.
            </p>
          </div>
          <Switch
            checked={!!pricing.whatsapp_pricing_confirmed}
            onCheckedChange={(v) => setPricing({ ...pricing, whatsapp_pricing_confirmed: !!v })}
            data-testid="sa-pricing-whatsapp-confirmed"
          />
        </div>
      </Card>

      <Card className="p-5 max-w-2xl space-y-4" data-testid="sa-pricing-experience-card">
        <h2 className="font-heading text-base font-semibold">Player experience</h2>
        <div className="space-y-1.5">
          <Label htmlFor="sa-pricing-redirect">Post-spin redirect URL</Label>
          <div className="relative">
            <ExternalLink className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="sa-pricing-redirect"
              type="url"
              inputMode="url"
              placeholder="https://citydooh.com"
              value={pricing.post_spin_redirect_url || ""}
              onChange={(e) =>
                setPricing({ ...pricing, post_spin_redirect_url: e.target.value })
              }
              className="pl-9"
              data-testid="sa-pricing-post_spin_redirect_url"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            After the win popup opens, players auto-redirect here in 10 seconds
            (or immediately if they click Continue / X). Must start with{" "}
            <code>http://</code> or <code>https://</code>.
          </p>
          {pricing.post_spin_redirect_url &&
            !URL_PREFIX_RE.test((pricing.post_spin_redirect_url || "").trim()) && (
              <p
                className="text-xs text-destructive"
                data-testid="sa-pricing-redirect-error"
              >
                URL must start with http:// or https://
              </p>
            )}
        </div>
      </Card>

      <Card className="p-5 max-w-2xl space-y-4" data-testid="sa-pricing-featured-card">
        <div className="flex items-start gap-3">
          <Trophy className="h-5 w-5 text-[#F5A800] mt-0.5" />
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold">Featured-slot marketplace</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Vendors pay to Boost their campaign into Top 1 / 2 / 3 within a (category, city). One flat price per rank across every city — matches the A4 scoping.
            </p>
          </div>
        </div>
        {featured ? (
          <>
            {[
              { key: "rank_1_rupees", label: "Rank 1 (top slot)", suffix: "₹ / 30-day slot" },
              { key: "rank_2_rupees", label: "Rank 2", suffix: "₹ / 30-day slot" },
              { key: "rank_3_rupees", label: "Rank 3", suffix: "₹ / 30-day slot" },
              { key: "duration_days", label: "Slot duration", suffix: "days each slot stays active after payment", integer: true, noRupee: true },
            ].map((f) => (
              <div key={f.key} className="flex items-center gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label>{f.label}</Label>
                  <div className="relative">
                    {!f.noRupee && <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                    <Input
                      type="number"
                      step={f.integer ? "1" : "1"}
                      min={0}
                      value={featured[f.key] ?? ""}
                      onChange={(e) => setFeatured({ ...featured, [f.key]: e.target.value })}
                      className={f.noRupee ? "" : "pl-9"}
                      data-testid={`sa-featured-${f.key}`}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{f.suffix}</p>
                </div>
              </div>
            ))}
            <div className="flex justify-end">
              <Button onClick={saveFeatured} disabled={featuredBusy} data-testid="sa-featured-save" className="bg-[#F5A800] text-[#0A0A0A] hover:bg-[#F5A800]/90">
                {featuredBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save featured pricing</>}
              </Button>
            </div>
          </>
        ) : (
          <div className="py-6 text-center text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading featured pricing…
          </div>
        )}
      </Card>

      <HomepageCapsCard />

      <Card className="p-5 max-w-2xl space-y-4" data-testid="sa-pricing-legacy-card">
        <h2 className="font-heading text-base font-semibold">Legacy fields</h2>
        <p className="text-xs text-muted-foreground -mt-2">
          Kept for backward-compat with older reports. Not used by the campaign-fee billing model.
        </p>
        {LEGACY_FIELDS.map(renderField)}
      </Card>

      <div className="max-w-2xl flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="sa-pricing-save">
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Save className="h-4 w-4 mr-1" /> Save pricing
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

const ChannelLockRow = ({ label, desc, value, onChange, testid }) => (
  <div className="flex items-start gap-3 rounded-lg border border-border p-3 bg-secondary/40">
    <MessageCircle className="h-5 w-5 text-primary mt-0.5" />
    <div className="flex-1">
      <Label className="text-sm font-semibold">{label} pricing confirmed</Label>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </div>
    <Switch
      checked={!!value}
      onCheckedChange={(v) => onChange(!!v)}
      data-testid={testid}
    />
  </div>
);


// ─────────────────────────────────────────────────────────────────
// Homepage display caps — global default (per-city overrides live on
// /franchise/homepage-editor). "None" here means "no cap". Editable
// by Admin + Super Admin.
// ─────────────────────────────────────────────────────────────────
const HomepageCapsCard = () => {
  const [caps, setCaps] = React.useState({ trending: "", offers: "", stores: "" });
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let live = true;
    api.adminGetHomepageCaps()
      .then((r) => {
        if (!live) return;
        const d = r.data || {};
        setCaps({
          trending: d.trending_display_count == null ? "" : String(d.trending_display_count),
          offers:   d.offers_display_count   == null ? "" : String(d.offers_display_count),
          stores:   d.stores_display_count   == null ? "" : String(d.stores_display_count),
        });
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const toInt = (s) => {
        if (s == null || String(s).trim() === "") return null;
        const n = Number(s);
        return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
      };
      await api.adminUpdateHomepageCaps({
        trending_display_count: toInt(caps.trending),
        offers_display_count:   toInt(caps.offers),
        stores_display_count:   toInt(caps.stores),
      });
      toast.success("Homepage display caps saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save caps");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-5 max-w-2xl space-y-4" data-testid="sa-homepage-caps-card">
      <div>
        <h2 className="font-heading text-base font-semibold">Homepage display caps</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Global default for the three consumer-homepage sections. Blank = uncapped (show everything). Franchise operators can override per-city on their own City Homepage editor.
        </p>
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { key: "trending", label: "Trending offers" },
            { key: "offers",   label: "All live offers" },
            { key: "stores",   label: "Popular stores" },
          ].map(({ key, label }) => (
            <div key={key} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <Input
                type="number"
                min="0"
                placeholder="Uncapped"
                value={caps[key]}
                onChange={(e) => setCaps((c) => ({ ...c, [key]: e.target.value }))}
                data-testid={`sa-cap-${key}`}
              />
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={busy || loading} data-testid="sa-caps-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save caps</>}
        </Button>
      </div>
    </Card>
  );
};
